import type {
  ListTicketsResponse,
  TicketState,
} from '~/api/k2/typedefs.autogen';

import { everest, k2 } from '~/services/serviceInstances';
import { Uuid } from '~/types/Uuid';

import { ParsedType } from '../../transformers/utils';

type Response = {
  ticket_id: Uuid;
  is_completed: boolean;
};

// K2 resolves a CRUID via the tenant-scoped GET /tickets list endpoint filtered
// by contract_record_unique_id. A match returns a single-item JSON:API
// collection; no match (or no access) returns an empty collection — never a 404.
const K2_COMPLETED_STATE: TicketState = 'COMPLETED';

type Request = {
  contractRecordUniqueId?: string;
  wssContractId?: Uuid;
  useK2?: boolean;
};

export const resolveWssTicketId = async ({
  contractRecordUniqueId,
  wssContractId,
  useK2,
}: Request): Promise<ParsedType<Response> | null> => {
  // K2 resolves solely by contract_record_unique_id and ignores wss_contract_id.
  // If only a wss_contract_id is available, fall through to the Everest path
  // rather than calling K2 with an undefined CRUID (which would 400 and bypass
  // the 404 Pilot fallback).
  if (useK2 && contractRecordUniqueId) {
    // K2 ignores wss_contract_id; it resolves solely by contract record unique
    // ID against the GET /tickets list endpoint.
    const response: ListTicketsResponse = await k2.get(`/tickets`, {
      params: {
        contract_record_unique_id: contractRecordUniqueId,
      },
    });

    const ticket = response.data?.[0];
    // No matching (or accessible) ticket — resolve to nothing so the contract
    // page renders blank rather than surfacing an error.
    if (!ticket) {
      return null;
    }

    return {
      ticketId: ticket.id,
      isCompleted: ticket.attributes.state === K2_COMPLETED_STATE,
    };
  }

  const response: Response = await everest.get(`/tickets/resolve-ticket-id/`, {
    params: {
      wss_contract_id: wssContractId,
      contract_record_unique_id: contractRecordUniqueId,
    },
  });

  return {
    ticketId: response.ticket_id,
    isCompleted: response.is_completed,
  };
};
